import { DOCUMENT, Injectable, REQUEST, inject } from '@angular/core'
import { Meta, Title } from '@angular/platform-browser'

import { TranslateService } from '@ngx-translate/core'

import { environment } from '../../../environments/environment'
import type { SeoPageMeta } from './seo.types'

const SITE_NAME = 'Uitgeverij Den Hollander'
const FALLBACK_IMAGE_PATH = '/assets/images/uitgeverij_den_hollander_logo.jpg'
const DESCRIPTION_MAX_LENGTH = 160

/** `og:locale` values for the languages the app serves. */
const OG_LOCALES: Record<string, string> = {
  nl: 'nl_NL',
  en: 'en_GB',
}

/**
 * The locales the app publishes a URL for. `nl` is unprefixed and is therefore
 * also the `x-default` (see app-routing.module.ts, which mounts the whole app
 * once for nl and once under /en).
 */
const INDEXED_LOCALES = ['nl', 'en'] as const
type IndexedLocale = (typeof INDEXED_LOCALES)[number]

/**
 * Meta tags this service owns. Every one of them is rewritten on every
 * navigation - a tag whose new value is empty is removed rather than left
 * behind, so a page without a description can't inherit the previous page's. A
 * list writes one tag per value under the same name.
 */
type ManagedTags = Record<string, string | (string | undefined)[] | undefined>

/** A `<link>` in the head that this service owns, keyed by rel + hreflang. */
interface HeadLink {
  rel: 'canonical' | 'alternate'
  hreflang?: string
  href: string | undefined
}

/**
 * Writes the document head (title, description, Open Graph, Twitter card,
 * canonical) from the `meta` fields the API's `seoPlugin` exposes.
 *
 * Called from `SeoTitleStrategy` rather than from the page components: the
 * router runs the title strategy for every navigation, including the initial
 * one during SSR, and it does so after resolvers have emitted - so the tags are
 * in the HTML the server sends out instead of appearing only after hydration.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly meta = inject(Meta)
  private readonly title = inject(Title)
  private readonly document = inject(DOCUMENT)
  private readonly translateService = inject(TranslateService)
  // Only ever non-null while handling a real SSR request; see
  // ssr-request-context.ts for the same caveat about REQUEST_CONTEXT.
  private readonly request = inject(REQUEST, { optional: true })

  apply(page: SeoPageMeta | null): void {
    const language = this.language()
    const title = text(page?.title) ?? SITE_NAME
    const description =
      truncate(text(page?.description), DESCRIPTION_MAX_LENGTH) ??
      this.defaultDescription()
    // Payload hands back absolute media URLs, but a relative one would be
    // relative to the API host rather than to this app.
    const image =
      absoluteUrl(page?.image?.url, environment.apiUrl) ??
      absoluteUrl(FALLBACK_IMAGE_PATH, this.origin())
    // Locale handling here is deliberately driven off the URL rather than off
    // the active translation language: this is about which URLs exist, and the
    // URL is what the language route guards themselves key on. (`language`
    // above describes the rendered content, and also covers the qqx
    // pseudo-locale, which has no URL of its own.)
    const currentPath = this.currentPath()
    const origin = this.origin()
    const barePath = page?.canonicalPath ?? localePath(currentPath, 'nl')
    // The URL actually being served. A share of an English page should still
    // resolve to the English page, even though the canonical points elsewhere.
    const pageUrl = absoluteUrl(
      localePath(barePath, localeOfPath(currentPath)),
      origin
    )
    // Deliberate product decision, and a departure from the usual advice: /en
    // is treated as a duplicate of the Dutch page rather than as a localized
    // variant, so every canonical points at the unprefixed nl URL. The
    // consequences are intended - Google consolidates each /en URL into its nl
    // counterpart and stops indexing it, and since an /en page no longer
    // canonicals to itself the hreflang cluster below is disregarded. Swap in
    // `pageUrl` here to restore self-referencing canonicals.
    const canonical = absoluteUrl(localePath(barePath, 'nl'), origin)
    const type = page?.type ?? 'website'
    const isArticle = type === 'article'
    const citation = page?.citation

    this.title.setTitle(
      title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`
    )
    this.setDocumentLanguage(language)
    this.syncLinks([
      { rel: 'canonical', href: canonical },
      // Every route is served under both locales, so each page points at its
      // counterpart, built from the same bare path in both languages. Retained
      // for other consumers; Google disregards them while the canonical above
      // points across languages.
      ...INDEXED_LOCALES.map(locale => ({
        rel: 'alternate' as const,
        hreflang: locale,
        href: absoluteUrl(localePath(barePath, locale), origin),
      })),
      {
        rel: 'alternate' as const,
        hreflang: 'x-default',
        href: absoluteUrl(localePath(barePath, 'nl'), origin),
      },
    ])
    this.syncTags({
      description,
      'og:site_name': SITE_NAME,
      'og:type': type,
      'og:locale': OG_LOCALES[language] ?? OG_LOCALES['nl'],
      'og:title': title,
      'og:description': description,
      'og:url': pageUrl,
      'og:image': image,
      'og:image:width': page?.image?.width?.toString(),
      'og:image:height': page?.image?.height?.toString(),
      'og:image:type': page?.image?.mimeType ?? undefined,
      // Only meaningful alongside og:type=article; emitted as undefined (and
      // therefore removed) for every other page type.
      'article:published_time': isArticle
        ? text(page?.publishedTime)
        : undefined,
      'article:modified_time': isArticle ? text(page?.modifiedTime) : undefined,
      'twitter:card': image ? 'summary_large_image' : 'summary',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': image,
      citation_title: text(citation?.title),
      citation_author: citation?.authors.map(text),
      citation_publication_date: text(citation?.publicationDate),
      citation_journal_title: text(citation?.journalTitle),
      citation_issn: citation?.issns.map(text),
      citation_volume: text(citation?.volume),
      citation_issue: text(citation?.issue),
      citation_firstpage: text(citation?.firstPage),
      citation_lastpage: text(citation?.lastPage),
    })
  }

  private syncTags(tags: ManagedTags): void {
    for (const [name, content] of Object.entries(tags)) {
      // Open Graph uses `property`, `description` and the Twitter card use
      // `name` - mixing them up makes the tag invisible to the crawler.
      const isProperty = name.startsWith('og:') || name.startsWith('article:')
      const values = [content]
        .flat()
        .filter((value): value is string => !!value)
      const existing = this.meta.getTags(
        isProperty ? `property='${name}'` : `name='${name}'`
      )

      existing.slice(values.length).forEach(tag => {
        this.meta.removeTagElement(tag)
      })
      values.forEach((value, index) => {
        const tag = existing.at(index)
        if (tag) {
          tag.setAttribute('content', value)
        } else {
          // Forced, or a second author with the same name would be dropped.
          this.meta.addTag(
            isProperty
              ? { property: name, content: value }
              : { name, content: value },
            true
          )
        }
      })
    }
  }

  private syncLinks(links: HeadLink[]): void {
    for (const { rel, hreflang, href } of links) {
      const selector = hreflang
        ? `link[rel='${rel}'][hreflang='${hreflang}']`
        : `link[rel='${rel}']`
      let link = this.document.head.querySelector<HTMLLinkElement>(selector)

      if (!href) {
        link?.remove()
        continue
      }

      if (!link) {
        link = this.document.createElement('link')
        link.setAttribute('rel', rel)
        if (hreflang) link.setAttribute('hreflang', hreflang)
        this.document.head.appendChild(link)
      }

      link.setAttribute('href', href)
    }
  }

  private setDocumentLanguage(language: string): void {
    // index.html can only carry one static value, and the app serves both nl
    // and en from the same document.
    this.document.documentElement.lang = language === 'qqx' ? 'nl' : language
  }

  private language(): string {
    return this.translateService.getCurrentLang() ?? 'nl'
  }

  private defaultDescription(): string | undefined {
    const key = 'seo.defaultDescription'
    const translation = String(this.translateService.instant(key))
    // The missing-translation handler echoes the key back (and does so for
    // every key under the qqx pseudo-locale).
    return translation === key ? undefined : translation
  }

  /**
   * Public origin of the running app.
   *
   * During SSR this is `REQUEST.url`, which Angular builds from the proxy
   * headers - but only when `server.ts` passes `trustProxyHeaders`, since it
   * strips those headers otherwise. Reading `x-forwarded-proto` here instead
   * cannot work: the app never receives the header.
   */
  private origin(): string {
    if (this.request) {
      return new URL(this.request.url).origin
    }

    return this.browserLocation()?.origin ?? ''
  }

  private currentPath(): string {
    if (this.request) {
      return new URL(this.request.url).pathname
    }

    return this.browserLocation()?.pathname ?? '/'
  }

  /**
   * `Document.location` is typed as always present, but the DOM implementation
   * used for the server render has no location outside of a real request (which
   * the REQUEST branches above cover anyway).
   */
  private browserLocation(): Location | undefined {
    const { location } = this.document as Partial<Document>
    return location
  }
}

/** The locale a URL path is served under; `nl` is the unprefixed default. */
function localeOfPath(path: string): IndexedLocale {
  return path === '/en' || path.startsWith('/en/') ? 'en' : 'nl'
}

/** Same page, addressed in `locale`. Accepts an already-prefixed path. */
function localePath(path: string, locale: IndexedLocale): string {
  const barePath = path === '/en' ? '/' : path.replace(/^\/en(?=\/)/, '')

  if (locale === 'nl') return barePath

  return barePath === '/' ? '/en' : `/en${barePath}`
}

/** Highest code point Unicode defines. */
const MAX_CODE_POINT = 0x10ffff

const HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
}

/**
 * Plain text fit for a meta tag's `content` attribute: some CMS fields (a
 * magazine's preface, for instance) hold rich text, and its entities have to be
 * decoded here - Angular escapes the attribute value again on the way out, so
 * an `&amp;` left in place would reach the crawler as a literal `&amp;`.
 */
function text(value: string | null | undefined): string | undefined {
  const normalized = value
    ?.replace(/<[^>]*>/g, ' ')
    .replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, decodeEntity)
    .replace(/\s+/g, ' ')
    .trim()

  return normalized === '' ? undefined : normalized
}

function decodeEntity(match: string, entity: string): string {
  if (!entity.startsWith('#')) {
    return HTML_ENTITIES[entity.toLowerCase()] ?? match
  }

  const isHex = entity[1].toLowerCase() === 'x'
  const codePoint = parseInt(entity.slice(isHex ? 2 : 1), isHex ? 16 : 10)

  // A CMS field can hold any numeric reference, including one out of Unicode's
  // range (`&#99999999;`), and `String.fromCodePoint` throws on those. That
  // would abort the title strategy mid-navigation and take a server render
  // with it, so anything undecodable is left as the author typed it. Surrogate
  // halves are refused as well: they don't throw, but they would put an
  // unpaired code unit in an attribute value.
  const isDecodable =
    !isNaN(codePoint) &&
    codePoint > 0 &&
    codePoint <= MAX_CODE_POINT &&
    !(codePoint >= 0xd800 && codePoint <= 0xdfff)

  return isDecodable ? String.fromCodePoint(codePoint) : match
}

function truncate(
  value: string | undefined,
  maxLength: number
): string | undefined {
  if (!value || value.length <= maxLength) return value

  const cut = value.slice(0, maxLength)
  const lastSpace = cut.lastIndexOf(' ')

  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}

function absoluteUrl(
  url: string | null | undefined,
  base: string
): string | undefined {
  const value = url?.trim()
  if (!value) return undefined
  if (/^https?:\/\//i.test(value)) return value
  if (!base) return value

  return `${base.replace(/\/+$/, '')}${value.startsWith('/') ? '' : '/'}${value}`
}
